import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Quote, Globe, Lightbulb, Leaf } from "lucide-react";
import { COMPANY, STATS, SERVICES, TESTIMONIALS, PRODUCTS } from "@/components/laneeka/data";
import { IMAGES } from "@/components/site/data";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay },
});

const FEATURES = [
  { icon: Globe, title: "Nationwide", sub: "Coverage" },
  { icon: Lightbulb, title: "Innovative", sub: "Ideas" },
  { icon: Leaf, title: "Environmental", sub: "Commitment" },
];

const CUSTOMERS = [
  { name: "RFL", logo: "/customers/rfl.png" },
  { name: "Olympic", logo: "/customers/olympic.png" },
  { name: "PRAN", logo: "/customers/pran.png" },
  { name: "Akij Group", logo: "/customers/akij.png" },
  { name: "Partex Group", logo: "/customers/partex.png" },
  { name: "ACI", logo: "/customers/aci.png" },
  { name: "Unilever", logo: "/customers/unilever.svg" },
];

export default function HomePage() {
  return (
    <main data-testid="home-page" className="pt-[108px]">
      <section className="relative bg-slate-950" data-testid="hero-section">
        <img src={IMAGES.heroFactory} alt="" aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-slate-950/60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <h1 data-testid="hero-headline" className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
              About Laneeka
            </h1>
            <p data-testid="hero-subtext" className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
              {COMPANY.name} is a leading provider of industrial belting solutions in Bangladesh, established
              in {COMPANY.established}. The company specializes in a wide range of high-quality conveyor systems
              and belts — PVC and PU conveyor belts, cotton canvas belts, plastic modular belts, felt belts,
              stainless steel mesh conveyor belts, and Teflon/PTFE seamless belts — plus plastic tabletop slat
              chains and U-turn curved conveyor belts.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/products" data-testid="hero-catalog-cta"
                className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-600 text-white text-xs font-bold uppercase tracking-wider px-7 py-3.5 transition-colors">
                View Our Services
              </Link>
              <Link to="/contact" data-testid="hero-quote-cta"
                className="inline-flex items-center gap-2 border border-slate-500 hover:border-white text-slate-200 hover:text-white text-xs font-bold uppercase tracking-wider px-7 py-3.5 transition-colors">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="relative border-t border-slate-800/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-800/80">
            {FEATURES.map((f) => (
              <div key={f.title} data-testid={`feature-${f.title.toLowerCase()}`} className="flex items-center gap-4 py-6 sm:px-8 first:pl-0">
                <f.icon className="w-7 h-7 text-blue-400 shrink-0" strokeWidth={1.5} />
                <p className="text-white font-bold uppercase tracking-widest text-sm">
                  {f.title} <span className="block text-xs font-medium normal-case tracking-normal text-slate-400">{f.sub}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-slate-200 py-8" data-testid="customers-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Our Customers:</span>
          {CUSTOMERS.map((c) => (
            <img
              key={c.name}
              src={c.logo}
              alt={`${c.name} logo`}
              data-testid={`customer-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="h-9 sm:h-11 w-auto object-contain grayscale hover:grayscale-0 transition-all"
            />
          ))}
        </div>
      </section>

      <section className="py-20 bg-[#faf6f1]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fade()} className="mb-10 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Transporting Across the Country</h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              We provide reliable belting and delivery services across the country. Our process is designed to
              make sure your goods keep moving safely, quickly, and on time.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s, i) => (
              <motion.div key={s.name} {...fade(i * 0.04)} data-testid={`service-card-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:border-blue-700 transition-colors">
                <h3 className="font-semibold text-slate-900">{s.name}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-950" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10 items-center">
          <motion.div {...fade()} className="lg:col-span-2">
            <img
              src="/team/founder-full.png"
              alt="Engr. Md. Lahabur Rahman — Founder & Managing Director"
              className="w-full max-w-sm mx-auto rounded-xl border border-slate-700 object-cover aspect-[4/5] bg-slate-900"
              data-testid="founder-photo"
            />
          </motion.div>
          <motion.div {...fade(0.1)} className="lg:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-400 mb-6">Message from the Founder</p>
            <blockquote className="text-xl sm:text-2xl font-medium text-white leading-relaxed" data-testid="founder-speech">
              "Every product we deliver and every service we provide is a testament to our commitment to quality,
              precision, and reliability. Laneeka International will continue to set new benchmarks in the conveyor
              belting industry — embracing cutting-edge technology, sustainable practices, and customer-centric solutions."
            </blockquote>
            <p className="mt-6 text-sm text-slate-400">
              <span className="block text-lg font-bold text-white">Engr. Md. Lahabur Rahman</span>
              Founder &amp; Managing Director, Laneeka International
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="featured-products">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fade()} className="mb-10 flex items-end justify-between gap-4">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Our Belting Products</h2>
            <Link to="/products" data-testid="featured-view-all"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800">
              View all 09 <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRODUCTS.slice(0, 3).map((p, i) => (
              <motion.div key={p.id} {...fade(i * 0.06)} data-testid={`featured-product-${p.id}`}
                className="group relative rounded-xl overflow-hidden border border-slate-200 aspect-[4/3]">
                <img src={p.image} alt={p.name} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-x-0 bottom-0 bg-slate-950/70 backdrop-blur-sm px-5 py-4">
                  <p className="text-white font-semibold">{p.name}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-[#faf6f1] border-y border-slate-200" data-testid="home-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6" >
          {STATS.map((s) => (
            <motion.div key={s.label} {...fade()} className="text-center">
              <p className="text-3xl font-bold text-blue-700">{s.value}</p>
              <p className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fade()} className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-700 mb-3">Testimonials</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">What our clients say</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.blockquote key={i} {...fade(i * 0.06)} data-testid={`testimonial-${i}`}
                className="bg-[#faf6f1] border border-slate-200 rounded-xl p-6">
                <Quote className="w-6 h-6 text-blue-700 mb-4" strokeWidth={1.75} />
                <p className="text-sm text-slate-600 leading-relaxed">{t.text}</p>
                <footer className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">{t.author}</footer>
              </motion.blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-950" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">Need a belt spec'd for your line?</h2>
            <p className="mt-2 text-slate-400">We respond to every request within 24 hours.</p>
          </div>
          <Link to="/contact" data-testid="home-cta-button"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-600 text-white text-xs font-bold uppercase tracking-wider px-7 py-3.5 transition-colors">
            Request a Quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
